import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { getFormValues } from "redux-form";
import classNames from "classnames";

import DynamicToggle from "./DynamicToggle";
import VariableNameInput from "./VariableNameInput";
import AssetSelect from "./AssetSelect";
import { useTooltipRebuild } from "hooks/useTooltipRebuild";

import "./DynamicAssetSelect.css";

export default function DynamicAssetSelect(props){
	const {
		defaultDynVarName,
		dynamicToggleDisabled,
		variableNameInputDisabled,
		variableNameInputTip,
		isAdStudio,
		...forwardedProps // We just need to separate these to forward to the Input without DOM warnings
	} = props;
	const { input, meta, disabled } = forwardedProps;
	const { onChange, onBlur } = input || {};
	const { name } = input || {};
	const [adType] = name.split(".");

	const creativeValues = useSelector(state => getFormValues(meta.form)(state)?.[adType] || {}); /*
		This makes some assumptions about the structure of our forms where we use this, which
		isn't ideal, but it's dramatically simpler than passing the adType in as a prop
	*/
	const isCreativeDynamic = creativeValues?.isDynamic;

	useTooltipRebuild(isCreativeDynamic);

	const initialDynVarName = (typeof(defaultDynVarName) === "function") ? defaultDynVarName(input, creativeValues) : defaultDynVarName;

	const value = input.value || {
		isDynamic: false,
		dynVarName: initialDynVarName,
	};

	if(!value.dynVarName){
		// We may get the value set from Shared Values without the default variable name
		value.dynVarName = initialDynVarName;
	}

	const { isDynamic } = value || {};

	const className = classNames({
		"dynamic-asset-select": true,
		"is-dynamic": isDynamic,
		disabled,
		[props.className]: !!props.className,
	});

	function onInputChange(newValue){
		if(newValue){
			const updatedValue = {
				...value,
				...newValue,
			};

			onChange?.(updatedValue);
		} else {
			onChange?.({
				isDynamic: value.isDynamic,
				dynVarName: value.dynVarName,
			});
		}
	}

	function onInputBlur(newValue){
		const updatedValue = {
			...value,
			...newValue,
		};
		onBlur?.(updatedValue);
	}

	function onToggleChange(){
		if(!disabled && !dynamicToggleDisabled){
			onChange({
				...value,
				isDynamic: !value.isDynamic,
			});
		}
	}

	function onVariableNameChange(newName){
		onChange({
			...value,
			dynVarName: newName,
		});
	}

	const inputProps = {
		...forwardedProps,
		input: {
			...props.input,

			name: "value",
			onChange: onInputChange,
			onBlur: onInputBlur,
			value,
		},
		className,
	};

	if(isAdStudio){
		inputProps.prompt = "Ad Studio";
		inputProps.iconName = "far fa-palette";
		inputProps.disabled = true;
		inputProps.thumbnailDisabled = true;
	} else if(isCreativeDynamic){
		const tipAction = isDynamic ? "Disable" : "Enable";
		const tip = `${tipAction} dynamic for pre-built assets.<br />Do not enable here if you wish to use<br />Ad Studio to create image assets.`;
		const detail = !isDynamic ? null : (<VariableNameInput
			value={value.dynVarName}
			onChange={onVariableNameChange}
			disabled={disabled || variableNameInputDisabled}
			tip={variableNameInputTip}
		/>);

		inputProps.pre = (
			<DynamicToggle
				onClick={onToggleChange}
				tip={tip}
				isDisabled={disabled || dynamicToggleDisabled}
				isDynamic={isDynamic}
			/>
		);
		inputProps.detail = detail;
	}

	return (
		<AssetSelect {...inputProps} />
	);
}

DynamicAssetSelect.displayName = "DynamicAssetSelect";

DynamicAssetSelect.defaultProps = {
	defaultDynVarName: "",
	dynamicToggleDisabled: false,
	variableNameInputDisabled: false,
	isAdStudio: false,
};

DynamicAssetSelect.propTypes = {
	defaultDynVarName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]), /*
		If this is provided as a function, it will receive the `input` prop, and
		the creative values as arguments, and it should return a string.

		This can be used to generate a default variable name based on the field name, etc.
		This is useful if you want to prefix a name with a card index, for example.
	*/
	input: PropTypes.object,
	meta: PropTypes.object,
	disabled: PropTypes.bool,
};
